/* Dedicated views for the 5 Disease Resistance analyses.
   Uses Recharts for charts. Matches dark-red BKAgriD theme.
*/
import React, { useMemo, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, ScatterChart, Scatter, ReferenceLine,
} from 'recharts';

const RED = '#991B1B';
const RED_ACTIVE = '#B91C1C';
const PALETTE = ['#991B1B', '#B91C1C', '#DC2626', '#EF4444', '#F87171', '#FCA5A5', '#7F1D1D', '#0369A1', '#059669', '#D97706'];

const fmt = (v, n = 4) => {
  if (v === null || v === undefined || v === '-') return '-';
  if (typeof v === 'number') return v.toFixed(n);
  return String(v);
};

function Citations() {
  return (
    <div className="rounded-xl p-4 mt-4" style={{ background: '#FEF2F2', border: '1px solid #FCA5A5' }} data-testid="disease-citations">
      <div className="text-xs uppercase tracking-wider font-bold mb-1" style={{ color: '#991B1B' }}>Materials & Methods — References</div>
      <ul className="text-xs space-y-0.5" style={{ color: '#7F1D1D' }}>
        <li>Shaner, G. & Finney, R.E. (1977). AUDPC. <i>Phytopathology</i> 67: 1051–1056.</li>
        <li>Campbell, C.L. & Madden, L.V. (1990). <i>Introduction to Plant Disease Epidemiology.</i></li>
        <li>Roelfs, A.P. (1992). Race monitoring via differential hosts. In <i>The Cereal Rusts.</i></li>
        <li>McIntosh, R.A. et al. (1995). <i>Wheat Rusts: An Atlas of Resistance Genes.</i></li>
        <li>Peterson, R.F. et al. (1948). Measuring rust infection. <i>Can. J. Res.</i> 26C: 496–500.</li>
      </ul>
    </div>
  );
}

function Card({ title, children, testid }) {
  return (
    <div className="rounded-xl p-4 bg-white" style={{ border: '1px solid #FCA5A5' }} data-testid={testid}>
      <h4 className="font-semibold text-sm uppercase tracking-wider mb-2" style={{ color: RED }}>{title}</h4>
      {children}
    </div>
  );
}

function Table({ rows, cols, highlightKey, highlightLabel }) {
  if (!rows || !rows.length) return <p className="text-xs text-gray-500">No data.</p>;
  const keys = cols || Object.keys(rows[0]);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse">
        <thead><tr className="bg-red-100 text-red-900">
          {keys.map(k => <th key={k} className="px-2 py-1.5 border border-red-200 text-left">{String(k).replace(/_/g, ' ')}</th>)}
        </tr></thead>
        <tbody>
          {rows.map((r, i) => {
            const hl = highlightKey && r[highlightKey];
            return (
              <tr key={i} className={hl ? 'bg-emerald-50' : (i % 2 === 0 ? 'bg-white' : 'bg-red-50')}>
                {keys.map(k => {
                  const v = r[k];
                  let s;
                  if (typeof v === 'boolean') s = v ? (highlightLabel || 'YES') : 'no';
                  else if (typeof v === 'number') s = fmt(v, k.toLowerCase().includes('p_') ? 6 : 4);
                  else s = String(v ?? '-');
                  return <td key={k} className="px-2 py-1 border border-red-200 font-mono">{s}</td>;
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* Classification chip */
function ClassChip({ cls }) {
  const colors = {
    Resistant: { bg: '#D1FAE5', fg: '#065F46' },
    'Moderately Resistant': { bg: '#DBEAFE', fg: '#1E40AF' },
    'Moderately Susceptible': { bg: '#FEF3C7', fg: '#92400E' },
    Susceptible: { bg: '#FEE2E2', fg: '#991B1B' },
  };
  const c = colors[cls] || { bg: '#E5E7EB', fg: '#374151' };
  return (
    <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: c.bg, color: c.fg }}>
      {cls}
    </span>
  );
}

/* ============================================================
   1. AUDPC
============================================================ */
export function AudpcView({ data }) {
  const chartData = useMemo(() => {
    if (!data?.progress_curves) return [];
    const timePoints = [...new Set(data.progress_curves.map(p => p.Time))].sort((a, b) => a - b);
    return timePoints.map(t => {
      const row = { Time: t };
      data.progress_curves.filter(p => p.Time === t).forEach(p => { row[p.Genotype] = p.Score; });
      return row;
    });
  }, [data]);
  if (!data) return null;
  const genotypes = data.ranked_table?.map(r => r.Genotype) || [];

  return (
    <div className="space-y-4" data-testid="audpc-view">
      <p className="text-sm bg-red-50 rounded-lg p-3 border border-red-200">{data.interpretation}</p>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {[
          ['Genotypes', data.n_genotypes],
          ['Scale max', data.scale_max],
          ['CD @ 5%', fmt(data.cd_5pct, 2)],
          ['CV %', fmt(data.cv_percent, 2)],
          ['Resistant', data.classification_summary?.Resistant || 0],
        ].map(([k, v]) => (
          <div key={k} className="rounded-lg p-3 bg-white border border-red-200">
            <div className="text-[10px] uppercase text-gray-500">{k}</div>
            <div className="font-mono text-base font-bold" style={{ color: RED }}>{v}</div>
          </div>
        ))}
      </div>

      <Card title="Disease Progress Curves" testid="audpc-chart">
        <div style={{ width: '100%', height: 340 }}>
          <ResponsiveContainer>
            <LineChart data={chartData}>
              <CartesianGrid stroke="#FECACA" />
              <XAxis dataKey="Time" tick={{ fontSize: 11 }} label={{ value: 'Time (DAS)', position: 'insideBottom', offset: -3 }} />
              <YAxis tick={{ fontSize: 11 }} label={{ value: 'Disease score', angle: -90, position: 'insideLeft' }} />
              <Tooltip />
              <Legend />
              {genotypes.slice(0, 10).map((g, i) => (
                <Line key={g} type="monotone" dataKey={g} stroke={PALETTE[i % PALETTE.length]} strokeWidth={2} dot={{ r: 3 }} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card title="AUDPC Ranking (lowest = most resistant)" testid="audpc-ranking">
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead><tr className="bg-red-100 text-red-900">
              {['Rank', 'Genotype', 'AUDPC', 'Relative AUDPC (%)', 'Classification', 'Group'].map(h => (
                <th key={h} className="px-2 py-1.5 border border-red-200 text-left">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {data.ranked_table.map((r, i) => (
                <tr key={i} className={r.Classification === 'Resistant' ? 'bg-emerald-50' : (i % 2 === 0 ? 'bg-white' : 'bg-red-50')}>
                  <td className="px-2 py-1 border border-red-200 font-mono">{r.Rank}</td>
                  <td className="px-2 py-1 border border-red-200 font-semibold">{r.Genotype}</td>
                  <td className="px-2 py-1 border border-red-200 font-mono">{r.AUDPC}</td>
                  <td className="px-2 py-1 border border-red-200 font-mono">{r.Relative_AUDPC_percent}</td>
                  <td className="px-2 py-1 border border-red-200"><ClassChip cls={r.Classification} /></td>
                  <td className="px-2 py-1 border border-red-200 text-center font-bold" style={{ color: RED }}>{r.Group}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="ANOVA" testid="audpc-anova"><Table rows={data.anova} /></Card>

      <div className="grid grid-cols-4 gap-2">
        {Object.entries(data.classification_summary || {}).map(([cls, n]) => (
          <div key={cls} className="rounded-lg p-3 bg-white border border-red-200 text-center">
            <ClassChip cls={cls} />
            <div className="font-mono text-xl font-bold mt-1" style={{ color: RED }}>{n}</div>
          </div>
        ))}
      </div>

      <Citations />
    </div>
  );
}

/* ============================================================
   2. INFECTION TYPE
============================================================ */
export function InfectionTypeView({ data }) {
  if (!data) return null;
  return (
    <div className="space-y-4" data-testid="infection-type-view">
      <p className="text-sm bg-red-50 rounded-lg p-3 border border-red-200">{data.interpretation}</p>
      {data.chi_square && (
        <Card title="Chi-square Test of Independence" testid="it-chi">
          <div className="flex flex-wrap gap-4 text-sm">
            <span>χ² = <b className="font-mono">{fmt(data.chi_square.chi2)}</b></span>
            <span>df = <b className="font-mono">{data.chi_square.df}</b></span>
            <span>p = <b className="font-mono">{fmt(data.chi_square.p_value, 6)}</b></span>
            <span className={`px-2 py-0.5 rounded text-xs font-bold ${data.chi_square.Sig === '**' ? 'bg-red-600 text-white' : data.chi_square.Sig === '*' ? 'bg-amber-500 text-white' : 'bg-gray-300 text-gray-700'}`}>
              {data.chi_square.Sig}
            </span>
          </div>
          <p className="text-xs mt-2 text-gray-700">{data.chi_square.interpretation}</p>
        </Card>
      )}
      <Card title="Infection-type Frequency Table" testid="it-table"><Table rows={data.frequency_table} /></Card>
      {data.virulence_frequency_per_isolate?.length > 0 && (
        <Card title="Virulence Frequency per Isolate" testid="it-isolates">
          <Table rows={data.virulence_frequency_per_isolate} />
        </Card>
      )}
      <Card title="Postulated Resistance Genes" testid="it-postulations">
        <div className="space-y-2">
          {data.postulated_genes.map(p => (
            <div key={p.Genotype} className="rounded-lg p-3 bg-red-50 border-l-4" style={{ borderLeftColor: RED }}>
              <div className="flex items-center justify-between">
                <span className="font-semibold">{p.Genotype}</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ background: 'white', color: RED, border: `1px solid ${RED}` }}>{p.Pattern}</span>
              </div>
              <div className="text-xs mt-1 text-gray-700">Likely: <b>{p.Likely_genes}</b></div>
            </div>
          ))}
        </div>
      </Card>
      <Citations />
    </div>
  );
}

/* ============================================================
   3. DISEASE-YIELD CORRELATION
============================================================ */
export function DiseaseYieldView({ data }) {
  if (!data) return null;
  const scatter = data.prediction_curve || [];
  return (
    <div className="space-y-4" data-testid="disease-yield-view">
      <p className="text-sm bg-red-50 rounded-lg p-3 border border-red-200">{data.interpretation}</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {[
          ['Slope', fmt(data.regression?.slope, 4)],
          ['R²', fmt(data.regression?.R2)],
          ['p-value', fmt(data.regression?.p_value, 6)],
          ['Loss / unit severity', fmt(data.yield_loss_per_unit_severity, 3)],
        ].map(([k, v]) => (
          <div key={k} className="rounded-lg p-3 bg-white border border-red-200">
            <div className="text-[10px] uppercase text-gray-500">{k}</div>
            <div className="font-mono text-base font-bold" style={{ color: RED }}>{v}</div>
          </div>
        ))}
      </div>
      <Card title="Regression Equation" testid="dy-regression">
        <p className="font-mono text-sm">{data.regression?.equation}</p>
        <div className="text-xs mt-1 text-gray-600">
          Pearson r = {fmt(data.regression?.pearson_r)} (p = {fmt(data.regression?.pearson_p, 6)}) · n = {data.regression?.n}
        </div>
      </Card>
      <Card title="Economic Threshold" testid="dy-eth">
        <div className="rounded-lg p-3 bg-amber-50 border border-amber-300 text-sm">
          <div><b>Economic threshold:</b> <span className="font-mono font-bold text-lg" style={{ color: RED }}>{fmt(data.economic_threshold?.economic_threshold_severity_percent, 2)}%</span> severity</div>
          <div className="text-xs text-gray-700 mt-1">
            Crop value: ₹{data.economic_threshold?.crop_value_per_kg}/kg · Control cost: ₹{data.economic_threshold?.control_cost_per_ha}/ha
          </div>
          <p className="text-xs mt-2">{data.economic_threshold?.note}</p>
        </div>
      </Card>
      <Card title="Yield Prediction Curve" testid="dy-chart">
        <div style={{ width: '100%', height: 300 }}>
          <ResponsiveContainer>
            <LineChart data={scatter}>
              <CartesianGrid stroke="#FECACA" />
              <XAxis dataKey="Severity" tick={{ fontSize: 11 }} label={{ value: 'Disease severity (%)', position: 'insideBottom', offset: -3 }} />
              <YAxis tick={{ fontSize: 11 }} label={{ value: 'Predicted yield', angle: -90, position: 'insideLeft' }} />
              <Tooltip />
              <Line type="monotone" dataKey="Predicted_Yield" stroke={RED_ACTIVE} strokeWidth={2} dot={false} />
              {data.economic_threshold?.economic_threshold_severity_percent != null && (
                <ReferenceLine x={data.economic_threshold.economic_threshold_severity_percent} stroke="#F59E0B" strokeDasharray="4 2"
                  label={{ value: `ETH (${fmt(data.economic_threshold.economic_threshold_severity_percent, 1)}%)`, fontSize: 10, fill: '#92400E' }} />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>
      <Citations />
    </div>
  );
}

/* ============================================================
   4. VIRULENCE FREQUENCY
============================================================ */
export function VirulenceFrequencyView({ data }) {
  const trendData = useMemo(() => {
    if (!data?.year_wise_trends) return [];
    const years = [...new Set(data.year_wise_trends.map(r => r.Year))].sort();
    return years.map(y => {
      const row = { Year: y };
      data.year_wise_trends.filter(r => r.Year === y).forEach(r => { row[r.Differential] = r.Virulence_percent; });
      return row;
    });
  }, [data]);
  if (!data) return null;
  const diffs = [...new Set((data.virulence_frequency_table || []).map(r => r.Differential))];

  return (
    <div className="space-y-4" data-testid="virulence-view">
      <p className="text-sm bg-red-50 rounded-lg p-3 border border-red-200">{data.interpretation}</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {[
          ['Isolates', data.diversity?.n_isolates],
          ['Patterns / races', data.diversity?.n_distinct_patterns_races],
          ['Shannon H′', fmt(data.diversity?.shannon_H)],
          ['Simpson D', fmt(data.diversity?.simpson_D)],
        ].map(([k, v]) => (
          <div key={k} className="rounded-lg p-3 bg-white border border-red-200">
            <div className="text-[10px] uppercase text-gray-500">{k}</div>
            <div className="font-mono text-base font-bold" style={{ color: RED }}>{v}</div>
          </div>
        ))}
      </div>

      {data.population_similarity && (
        <Card title="Population Similarity (first vs last year)" testid="vf-similarity">
          <div className="rounded-lg p-3 bg-red-50">
            <div className="text-sm">
              {data.population_similarity.first_year} vs {data.population_similarity.last_year} —
              Rogers distance = <b className="font-mono">{fmt(data.population_similarity.roger_distance)}</b> ·
              Similarity = <b className="font-mono">{fmt(data.population_similarity.similarity_percent, 2)}%</b>
            </div>
            <p className="text-xs mt-1 text-gray-600">
              Lower similarity = population has shifted virulence spectrum (new races emerging).
            </p>
          </div>
        </Card>
      )}

      <Card title="Virulence Frequency per Differential Host" testid="vf-table">
        <Table rows={data.virulence_frequency_table} />
      </Card>

      <Card title="Virulence Trend Over Years" testid="vf-chart">
        <div style={{ width: '100%', height: 320 }}>
          <ResponsiveContainer>
            <LineChart data={trendData}>
              <CartesianGrid stroke="#FECACA" />
              <XAxis dataKey="Year" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} label={{ value: 'Virulence (%)', angle: -90, position: 'insideLeft' }} />
              <Tooltip />
              <Legend />
              {diffs.map((d, i) => (
                <Line key={d} type="monotone" dataKey={d} stroke={PALETTE[i % PALETTE.length]} strokeWidth={2} dot={{ r: 3 }} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Citations />
    </div>
  );
}

/* ============================================================
   5. RESISTANCE SCREENING SUMMARY
============================================================ */
export function ResistanceScreeningView({ data }) {
  const [disease, setDisease] = useState(data?.diseases?.[0]);
  if (!data) return null;
  const d = data.per_disease?.[disease];

  // Heatmap cell class
  const cellColor = (val, threshold) => {
    if (val == null || typeof val !== 'number') return 'bg-gray-100 text-gray-500';
    if (val <= threshold * 0.5) return 'bg-emerald-200 text-emerald-900';
    if (val <= threshold) return 'bg-emerald-100 text-emerald-800';
    if (val <= threshold * 1.5) return 'bg-amber-100 text-amber-800';
    if (val <= threshold * 2) return 'bg-red-200 text-red-900';
    return 'bg-red-400 text-white';
  };

  return (
    <div className="space-y-4" data-testid="resistance-screening-view">
      <p className="text-sm bg-red-50 rounded-lg p-3 border border-red-200">{data.interpretation}</p>
      <div className="rounded-lg p-3 bg-emerald-50 border border-emerald-300 inline-block">
        <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-900">Total consistent-resistant genotypes</div>
        <div className="font-mono text-2xl font-bold text-emerald-800">{data.total_consistent_resistant_genotypes}</div>
      </div>

      {data.diseases.length > 1 && (
        <div className="flex gap-2 flex-wrap">
          {data.diseases.map(ds => (
            <button key={ds} onClick={() => setDisease(ds)}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold transition"
              style={{ background: disease === ds ? RED_ACTIVE : 'white', color: disease === ds ? 'white' : '#374151', border: '1px solid #FCA5A5' }}
              data-testid={`rs-disease-tab-${ds}`}>
              {ds}
            </button>
          ))}
        </div>
      )}

      {d && (
        <>
          <Card title={`Master Resistance Heatmap — ${disease} (threshold ≤ ${d.resistance_threshold})`} testid="rs-heatmap">
            <div className="overflow-x-auto">
              <table className="text-xs border-collapse">
                <thead><tr className="bg-red-100 text-red-900">
                  <th className="px-2 py-1.5 border border-red-200 sticky left-0 bg-red-100">Genotype</th>
                  {d.environments.map(e => <th key={e} className="px-2 py-1.5 border border-red-200">{e}</th>)}
                  <th className="px-2 py-1.5 border border-red-200">Mean</th>
                  <th className="px-2 py-1.5 border border-red-200">SD</th>
                  <th className="px-2 py-1.5 border border-red-200">Max</th>
                  <th className="px-2 py-1.5 border border-red-200">Consistent</th>
                </tr></thead>
                <tbody>
                  {d.master_table.map((r, i) => (
                    <tr key={i} className={r.Consistent_resistant ? 'bg-emerald-50' : ''}>
                      <td className="px-2 py-1 border border-red-200 font-semibold sticky left-0 bg-inherit">{r.Genotype}</td>
                      {d.environments.map(e => {
                        const v = r[e];
                        return <td key={e} className={`px-2 py-1 border border-red-200 font-mono text-center ${cellColor(v, d.resistance_threshold)}`}>{fmt(v, 2)}</td>;
                      })}
                      <td className="px-2 py-1 border border-red-200 font-mono text-center">{fmt(r.Mean, 2)}</td>
                      <td className="px-2 py-1 border border-red-200 font-mono text-center">{fmt(r.SD, 2)}</td>
                      <td className="px-2 py-1 border border-red-200 font-mono text-center">{fmt(r.Max, 2)}</td>
                      <td className="px-2 py-1 border border-red-200 text-center">
                        {r.Consistent_resistant
                          ? <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-600 text-white">YES</span>
                          : <span className="px-1.5 py-0.5 rounded text-[10px] bg-gray-300 text-gray-700">no</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card title="Top 10 Most Resistant Genotypes" testid="rs-top10">
            <Table rows={d.top_10_most_resistant.map(r => ({
              Genotype: r.Genotype, Mean: r.Mean, SD: r.SD, Max: r.Max,
              Consistent_resistant: r.Consistent_resistant,
            }))} highlightKey="Consistent_resistant" highlightLabel="✓" />
          </Card>

          {d.consistent_resistant_genotypes.length > 0 && (
            <Card title="Consistent-resistant Genotypes (resistant in every environment)" testid="rs-consistent">
              <div className="flex flex-wrap gap-2">
                {d.consistent_resistant_genotypes.map(g => (
                  <span key={g} className="px-3 py-1.5 rounded-full text-xs font-bold bg-emerald-600 text-white">{g}</span>
                ))}
              </div>
            </Card>
          )}
        </>
      )}

      <Citations />
    </div>
  );
}
